import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import RCore from 'RCore';
import _ from 'lodash';
import config from 'cfg';
import { colorForTeam } from '../../../../helpers/TeamColorProfile';
import { numberForDriver } from '../../../../helpers/NumberForDriver';

//These lines are needed to get common i18
const i18Common = require('../../../../i18/i18.Common');
const i18 = RCore.getTranslation.bind({ dict: i18Common });

const SkewContainer = styled.div`
    --size-row: ${props => (props.display === 'large' ? '44px' : '27px')};
    margin-right: 15px;

    table {
        font-size: ${props => (props.display === 'large' ? '30px' : '16px')};
        width: 100%;
        border-collapse: separate;
        border-spacing: 0 3px;

        .f1-cell-header {
            &.f1-cell-teamName {
                span {
                    font-weight: 400;
                }
            }
            &.f1-cell-comment {
                text-align: right;
            }
        }
    }

    ${props => props.display === 'large' && `* {text-transform: none}`};
`;

const Cell = styled.span`
    position: relative;
    height: var(--size-row);
    line-height: var(--size-row);
    display: block;
    padding: 0 15px;
    text-align: ${props => props.textAlign || 'center'};
    color: ${props => props.theme.f1.tableTextColor};
    &:before {
        transform: skew(-15deg);
        z-index: 1;
        position: absolute;
        content: '';
        left: -1px;
        top: 0px;
        bottom: 0px;
        right: -1px;
        background: ${props => props.theme.f1.tableBgColor};
        z-index: -1;
    }
    img {
        height: var(--size-row);
    }
`;

const DarkCell = Cell.extend`
    color: ${props => props.theme.f1.headerTextColor};
    &:before {
        background: ${props => props.theme.f1.headerBgColor};
    }
`;

const LiveCell = Cell.extend`
    color: ${props => props.theme.color};
    &:before {
        background: ${props => props.theme.positiveColor};
    }
`;

const HighCell = Cell.extend`
    color: ${props => props.theme.f1.primaryTextColor};
    &:before {
        background: ${props => props.theme.f1.primaryColor};
    }
`;

const DiffCell = Cell.extend`
    min-width: 75px;
    text-align: center;
    color: ${props => props.theme.positiveColor};
    font-weight: bold;
    span {
        position: relative;
        margin-left: 14px;
        &::after {
            content: '';
            position: absolute;
            content: '';
            left: -14px;
            top: 50%;
            transform: translateY(-50%);
            width: 0;
            height: 0;

            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 8px solid ${props => props.theme.positiveColor};
        }
    }

    &.f1-pos-down {
        color: ${props => props.theme.negativeColor};
        span::after {
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-bottom: 0px solid transparent;
            border-top: 8px solid ${props => props.theme.negativeColor};
        }
    }
    &.f1-pos-zero {
        color: ${props => props.theme.f1.tableTextColor};
        span::after {
            border-left: 0 solid transparent;
            border-right: 8px solid ${props => props.theme.f1.tableTextColor};
            border-bottom: 5px solid transparent;
            border-top: 5px solid transparent;
        }
    }
`;

const F1Row = styled.tr`
    position: relative;

    padding: 0;

    align-items: center;
    z-index: 2;
    margin-bottom: 10px;
    text-transform: uppercase;
    color: ${props => props.theme.color};

    td {
        margin: 0;
        margin-bottom: 2px;
        line-height: var(--size-row);
        padding: 0px;
        font-style: italic; 
        white-space: nowrap;
    }
    .f1-cell-pos span {
        padding: 0 5px;
    }
    .f1-cell-name,
    .f1-cell-teamName,
    .f1-cell-driverName {
        width: 100%;
    }
    .f1-cell-teamName {
        span {
            font-weight: bold;
        }
    }
    .f1-cell-pits {
        width: 60px;
        max-width: 60px;
    }   
    .f1-cell-pos {
        width: 40px;
        .f1-pos {
            z-index: 5;
            
            &:after {
                transform: skew(-15deg);
                z-index: 1;
                position: absolute;
                content: '';
                width: 5px;
                top: 0px;
                bottom: 0px;
                right: -5px;
                background: ${props => colorForTeam(props.team, 'color')};
                z-index: -1;
            }
        }
    }
    .f1-cell-pits,
    .f1-cell-points,
    .f1-cell-rank {
        width: 40px;
        .f1-pits,
        .f1-points,
        .f1-rank {
            z-index: 5;
            &:after {
                transform: skew(-15deg);
                z-index: 1;
                position: absolute;
                content: '';
                width: 5px;
                top: 0px;
                bottom: 0px;
                right: -5px;
                background: ${props => colorForTeam(props.team, 'color')};
                z-index: -1;
            }
        }
    }

    .f1-cell-pits,
    .f1-cell-points {
        .f1-pits,
        .f1-points {
            &:after {
                right: auto;
                left: -5px;
            }
        }
    }
    
    .f1-qual {
        padding-left: 0;
        text-align: left;
        min-width: 120px
    }
    
    .f1-qual-q {
        display: inline-block;
        padding: 0 5px;
        margin: 0 5px;
        position: relative;
        &:after {
            text-align: center;
            z-index: 1;
            position: absolute;
            content: attr(data-text);
            left: 0px;
            top: 0px;
            bottom: 0px;
            right: 0px;
            line-height: var(--size-row);
            color: #fff;
        }
        &:before {
            transform: skew(-15deg);
            z-index: 1;
            position: absolute;
            content: '';
            left: 0px;
            top: 0px;
            bottom: 0px;
            right: 0px;
            color: #fff;
            
        }
        &.f1-qual-q1:before {
            background: #00acac;
        }
        &.f1-qual-q2:before {
            background: #221248;
        }
        &.f1-qual-q3:before {
            background: #f61216;
        }
    }
    .f1-cell-tyre {
        & > span {
            display: flex;
            align-items: center;
            .tyre-symb {
                padding 0 5px;
            }
        }
        
    }
    .f1-cell-bolid {
        min-width: 250px;
    }

    .f1-empty {
        display: none;
        background: transparent;
    }

    .f1-cell-driverNumber {
        width: 45px;

        span {
            padding-right: 0;
        }
    }
`;

const HeaderRow = F1Row.extend`
    td span {
        color: ${props => props.theme.f1.headerTextColor};
        &:before {
            background: ${props => props.theme.f1.headerBgColor};
        }
    }
    .f1-cell-rank {
        .f1-rank {
            &:before {
                border-right: 4px solid ${props => props.theme.f1.headerBgColor};
            }
        }
    }
    .f1-cell-pos {
        .f1-pos {
            &:before {
                border-right: 4px solid ${props => props.theme.f1.headerBgColor};
            }
        }
    }
`;
let renderVal = (key, value, obj) => {
    switch (key) {
        case 'gpPoints': {
            if (value === 0) return <Cell className="f1-empty" />;
            return <HighCell className={`f1-${key}`}>{value}</HighCell>;
        }
        case 'out':
            return <HighCell className={`f1-${key}`}>{value}</HighCell>;
        case 'rank':
        case 'pits':
        case 'points':
            return <DarkCell className={`f1-${key}`}>{value}</DarkCell>;
        case 'driverNumber':
            return <Cell className={`f1-${key}`}>{numberForDriver(value.shortName, value.year)}</Cell>;
        case 'pos':
            return obj.out ? (
                <HighCell className={`f1-${key}`}>{value}</HighCell>
            ) : (
                <DarkCell className={`f1-${key}`}>{value}</DarkCell>
            );
        case 'comment':
        case 'time':
        case 'gap':
            return (
                <Cell className={`f1-${key}`} textAlign={'right'}>
                    {value}
                </Cell>
            );
        case 'teamShortName':
        case 'teamName': {
            return (
                <Cell className={`f1-${key}`} textAlign={'left'}>
                    {value}
                </Cell>
            );
        }
        case 'name':
        case 'driverName': {
            const name = RCore.getFirstLastName(value);
            return (
                <Cell className={`f1-${key}`} textAlign={'left'}>
                    {name.firstName} <strong>{name.lastName}</strong>
                </Cell>
            );
        }
        case 'in_pit':
            return value !== 'YES' ? (
                <LiveCell className={`f1-${key} f1-${key}-${value}`}>ON TRACK</LiveCell>
            ) : (
                <HighCell>INPIT</HighCell>
            );

        case 'q1':
        case 'q2':
        case 'q3':
            return (
                <Cell className={`f1-qual ${key}`}>
                    <span className={`f1-qual-q f1-qual-${key}`} data-text={key}>
                        {key}
                    </span>
                    <span className="f1-qual-time">{value}</span>
                </Cell>
            );
        case 'posChange':
        case 'diff': {
            if (value === 0)
                return (
                    <DiffCell className="f1-pos-zero">
                        <span>{value}</span>
                    </DiffCell>
                );
            if (value < 0)
                return (
                    <DiffCell className="f1-pos-down">
                        <span>{value}</span>
                    </DiffCell>
                );
            if (value > 0)
                return (
                    <DiffCell className="f1-pos-up">
                        <span>+{value}</span>
                    </DiffCell>
                );
            if (!value) return;
        }
        case 'bolid':
            return (
                <Cell textAlign="right">
                    <img src={`http://cdn.tjekscores.dk/f1/cars/${value}_rev.png`} alt={value} height="27" />
                </Cell>
            );

        case 'tyre': {
            return value ? (
                <Cell>
                    <img height="22px" src={`${config.cdn.cdnUrl}/f1/tyre-types/${value}.png`} alt="" />
                    <span className="tyre-symb">{value}</span>
                </Cell>
            ) : null;
        }
        default:
            return <Cell>{value}</Cell>;
    }
};

const renderHeader = (key, value) => {
    switch (key) {
        case 'gap':
        case 'time':
            return (
                <Cell className={`f1-${key}`} textAlign={'right'}>
                    {value}
                </Cell>
            );
        case 'name':
        case 'teamShortName':
        case 'teamName':
        case 'driverName': {
            return (
                <Cell className={`f1-${key}`} textAlign={'left'}>
                    {value}
                </Cell>
            );
        }
        default:
            return <DarkCell>{value}</DarkCell>;
    }
};

const Header = ({ spec }, ctx) => {
    let lang = _.get(ctx, 'info.lang', 'en');

    return (
        <HeaderRow>
            {spec.map(key => {
                if (key === 'end')
                    return (
                        <td key={key} className="f1-cell-end">
                            <span />
                        </td>
                    );
                return (
                    <td key={key} className={`f1-cell-${key} f1-cell-header`}>
                        {renderHeader(key, i18(`f1.table.${key}`, lang))}
                    </td>
                );
            })}
        </HeaderRow>
    );
};

const Row = ({ driver, spec, ...props }) => {
    let values = driver || props.values || props.rowData;

    return (
        <F1Row
            key={values.id}
            className={`f1-team-${values['teamShortName']}`}
            team={values['teamShortName']}
            {...props}
        >
            {spec.map(key => {
                if (values[key] === 'empty') return <td key={key} className="f1-empty" />;
                if (key === 'end')
                    return (
                        <td key={key} className="f1-cell-end">
                            <span />
                        </td>
                    );
                return (
                    <td key={key} className={`f1-cell-${key}`}>
                        {renderVal(key, values[key], values)}
                    </td>
                );
            })}
        </F1Row>
    );
};

const HrRow = ({ length }) => (
    <tr key="elimination">
        <td colSpan={length}>
            <div
                style={{
                    height: '5px',
                    backgroundColor: 'red'
                }}
            />
        </td>
    </tr>
);

Header.contextTypes = {
    info: PropTypes.object
};

Row.contextTypes = {
    info: PropTypes.object
};

const Container = ({ children, display }) => (
    <SkewContainer display={display}>
        <table>
            <tbody>{children}</tbody>
        </table>
    </SkewContainer>
);

export default { Header, Row, HrRow, Container };



/** WEBPACK FOOTER **
 ** ../src/widgets/components/sm/views/f1/F1SkewRow.js
 **/